<script setup>
import { computed, getCurrentInstance, h, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
/* https://flowbite.com/docs/forms/input-field/ */
const props = defineProps({
    panels: {
        type: Array,
        required: true
    },
    action: {
        type: String,
        default: 'regist'
    },
    nonButton: {
        // 이전 다음을 표시하지 않으려고함
        type: Boolean,
        default: true
    },
    containerMargin: {
        type: Object,
        default: () => ({
            left: '0rem',
            right: '0rem'
        })
    },
    contentWidth: {
        type: String,
        default: '30rem'
    },
    contentHeight: {
        type: String,
        default: '45rem'
    },
    isPopupVisible: {
        type: Boolean,
        default: false
    }
});

const panels = ref(props.panels);
const currentStep = ref('1');
const componentValues = ref({});
const actionRefs = ref(props.action);
const componentRefs = ref({});
const emit = defineEmits(['input', 'click', 'change', 'navigation', 'buttonClick', 'update:isPopupVisible']);
const hiddenState = ref({});
const isPopupVisibleRefs = ref(props.isPopupVisible);
const nonButtonRefs = ref(props.nonButton);
const panelPoint = ref(null);
const popupContent = ref(null);
let offsetX = 0;
let offsetY = 0;
let isDragging = false;

const updateStep = (newStep) => {
    currentStep.value = newStep;
};

const handleNavigation = (direction) => {
    const currentIndex = parseInt(currentStep.value);
    let newStep;

    if (direction === 'prev' && currentIndex > 1) {
        newStep = (currentIndex - 1).toString();
    } else if (direction === 'next' && currentIndex < props.panels.length) {
        newStep = (currentIndex + 1).toString();
    } else {
        return;
    }

    updateStep(newStep);
    emit('navigation', { currentIndex, newStep });
};

const handleButtonClick = (action) => {
    emit('buttonClick', action);
};

watch(
    () => props.action,
    (newValue) => {
        actionRefs.value = newValue;
    }
);

watch(
    () => props.isPopupVisible,
    (newValue) => {
        isPopupVisibleRefs.value = newValue;
    }
);

const componentMap = computed(() => {
    const map = {};
    const app = getCurrentInstance().appContext.app;
    const globalComponents = app._context.components;
    Object.keys(globalComponents).forEach((name) => {
        const key = name.toLowerCase();
        map[key] = globalComponents[name];
    });
    return map;
});

const renderRow = (row) => {
    const validComponents = row.components.filter((comp) => comp && comp.type !== 'empty');
    //console.log('Panel buttons:', props.panels[currentStep.value - 1].buttons);
    return h(
        'div',
        {
            class: 'flex justify-start mb-1',
            style: { marginTop: row.marginTop || '0rem' }
        },
        validComponents.map((component, index) => renderComponent(component, index, validComponents))
    );
};

const renderComponent = (component, index, rowComponents) => {
    if (!component || component.type === 'empty') return null;

    const componentStyle = {
        width: component.width || `${100 / rowComponents.length}%`,
        marginLeft: component.marginLeft || '0rem',
        marginRight: component.marginRight || '0rem',
        display: isHidden(component.props?.inputId) ? 'none' : 'block'
    };

    return h('div', { style: componentStyle }, [
        h(renderDynamicComponent(component.type), {
            ...component.props,
            modelValue: componentValues.value[component.props?.inputId],
            'onUpdate:modelValue': (value) => handleInputEvent(component.props?.inputId, value, component.type),
            onInput: (event) => handleEvent('input', component.inputEvent, event),
            onClick: (event) => handleEvent('click', component.inputEvent, event),
            onChange: (event) => handleEvent('change', component.inputEvent, event),
            ref: (el) => {
                if (el && component.props?.inputId) {
                    componentRefs.value[component.props.inputId] = el;
                }
            }
        })
    ]);
};

const renderDynamicComponent = (component) => {
    if (typeof component === 'string') {
        return componentMap.value[component.toLowerCase()] || null;
    }
    return component;
};

const handleEvent = (event, handler, ...args) => {
    //console.log(`Event: ${event}, Handler: ${handler}, Args: ${args}`);
    if (args[0] instanceof PointerEvent || args[0] instanceof Event) {
        return;
    }

    if (handler) {
        handler(...args);
        // if (args[0] instanceof Object) componentValues.value[args[0].id] = args[0];
    }
    emit(event, ...args);
};

const handleClickEvent = (key, value) => {
    emit('click', key, value);
};

const handleInputEvent = (key, value, type) => {
    if (key && key !== 'undefined') {
        if (typeof value === 'object' && value !== null && 'value' in value) {
            componentValues.value[key] = value.value;

            const component = findComponentByInputId(key);
            if (component && component.props.checkedid) {
                componentValues.value[component.props.checkedid] = value.checked ? 'Y' : 'N';
                emit('input', component.props.checkedid, value.checked);
            }

            emit('input', key, value.value);
        } else {
            // if (type === 'uploadbox') {

            // }

            // 다른 컴포넌트들의 경우
            componentValues.value[key] = value;
            emit('input', key, value);
        }
    } else {
        console.error('Invalid key for input event:', key);
    }
};

const findComponentByInputId = (inputId) => {
    for (const panel of props.panels) {
        if (Array.isArray(panel.content)) {
            for (const row of panel.content) {
                const validComponents = row.components.filter((comp) => comp && comp.type !== 'empty');

                for (const component of validComponents) {
                    if (component.props && component.props.inputId === inputId) {
                        return component;
                    }
                }
            }
        } else if (typeof panel.content === 'string') {
            if (panel.title === inputId) return panel;
        }
    }
    return null;
};

const handleChangeEvent = (key, value) => {
    if (key && key !== 'undefined') {
        emit('change', key, value);
    }
};

onMounted(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    initializeComponent();
    componentValues.value = getInitialValues();
    //console.log(componentValues.value);
    currentStep.value = '1';
});

const handleEscKey = (event) => {
    if (event.key === 'Escape') {
        closePopup();
    }
};

onMounted(() => {
    window.addEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
    window.removeEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
});
const onMouseDown = (event) => {
    if (popupContent.value) {
        const rect = popupContent.value.getBoundingClientRect();
        offsetX = event.clientX - rect.left;
        offsetY = event.clientY - rect.top;
        isDragging = true;
    }
};

const onMouseMove = (event) => {
    if (isDragging && popupContent.value) {
        const newX = event.clientX - offsetX;
        const newY = event.clientY - offsetY;
        popupContent.value.style.left = `${newX}px`;
        popupContent.value.style.top = `${newY}px`;
    }
};

const onMouseUp = () => {
    isDragging = false;
};

const initializeComponent = () => {
    const initializePanel = (panel) => {
        return new Promise((resolve) => {
            const initializeValue = (component) => {
                if (component && component.props) {
                    return new Promise((resolveComponent) => {
                        nextTick(() => {
                            const inputId = component.props.inputId;
                            switch (component.type) {
                                case 'radiobutton':
                                case 'combobox':
                                case 'brandcombobox':
                                case 'multicombobox':
                                case 'selectbox':
                                    component.props.modelValue = '';
                                    break;
                                case 'vehiclegroup':
                                case 'vehicleoption':
                                    component.props.modelValue = [];
                                    break;
                                case 'checkbox':
                                    component.props.modelValue = 'N';
                                    break;
                                case 'uploadbox':
                                    component.props.fileId = '';
                                    component.props.filename = '';
                                    component.props.originalname = '';
                                    component.props.modelValue = [];
                                    break;
                                case 'multiuploadbox':
                                    component.props.fileId = '';
                                    component.props.modelValue = [];
                                    // if (!Array.isArray(component.props.modelValue)) {
                                    //     component.props.modelValue = component.props.modelValue ? [component.props.modelValue] : [];
                                    // }
                                    break;
                                case 'area':
                                    component.props.modelValue = [];
                                    component.props.keyVal = '';
                                    break;
                                case 'vehicleno':
                                    component.props.modelValue = '';
                                    component.props.keyVal = '';
                                    break;
                                case 'notelist':
                                    component.props.modelValue = '';
                                    component.props.dataList = [];
                                    break;
                                default:
                                    component.props.modelValue = '';
                            }
                            component.props.errmsg = '';
                            component.props.readonly = false;

                            if (inputId) {
                                componentValues.value[inputId] = component.props.modelValue;
                            }
                            resolveComponent();
                        });
                    });
                }
                return Promise.resolve();
            };

            const processContent = (content) => {
                const contentPromises = [];
                if (Array.isArray(content)) {
                    content.forEach((row) => {
                        if (Array.isArray(row.components)) {
                            row.components.forEach((component) => {
                                contentPromises.push(initializeValue(component));
                            });
                        }
                    });
                }
                return Promise.all(contentPromises);
            };

            processContent(panel.content).then(() => {
                resolve();
            });
        });
    };

    const initPromises = props.panels.map(initializePanel);

    return Promise.all(initPromises).then(() => {
        // console.log('All components initialized');
        // console.log('Component values:', componentValues.value);
    });
};

const getAllComponentValues = () => {
    return JSON.parse(JSON.stringify(componentValues.value));
};

const getModelValue = (inputId) => {
    if (Object.hasOwn(componentValues.value, inputId)) {
        // componentValues.value.hasOwnProperty(inputId)
        return componentValues.value[inputId] || '';
    }
    return '';
};

const getExposeValues = () => {
    const exposeValues = {};
    props.panels.forEach((panel) => {
        if (Array.isArray(panel.content)) {
            panel.content.forEach((row) => {
                if (Array.isArray(row.components)) {
                    row.components.forEach((component) => {
                        if (component && component.props && component.props.inputId) {
                            const inputId = component.props.inputId;
                            const componentRef = componentRefs.value[inputId];
                            if (componentRef) {
                                exposeValues[inputId] = { ...componentRef };
                            }
                        }
                    });
                }
            });
        }
    });

    return exposeValues;
};

const getStepComponentValues = (stepIndex = currentStep.value) => {
    const stepValues = {};
    const panel = props.panels[stepIndex - 1]; // stepIndex는 1부터 시작하므로 1을 빼줍니다.

    if (panel && Array.isArray(panel.content)) {
        panel.content.forEach((row) => {
            if (Array.isArray(row.components)) {
                row.components.forEach((component) => {
                    if (component && component.props && component.props.inputId) {
                        const inputId = component.props.inputId;
                        if (componentValues.value.hasOwnProperty(inputId)) {
                            stepValues[inputId] = componentValues.value[inputId];
                        }
                    }
                });
            }
        });
    }

    return stepValues;
};

const getInitialValues = () => {
    const initialValues = {};

    const processComponent = (component) => {
        if (component && component.props && component.props.inputId) {
            const inputId = component.props.inputId;
            switch (component.type) {
                case 'radiobutton':
                case 'combobox':
                case 'brandecombobox':
                case 'multicombobox':
                case 'selectbox':
                    initialValues[inputId] = component.props.modelValue || (component.props.options && component.props.options.length > 0 ? component.props.options[0].value : '');
                    break;
                // case 'vehiclegroup':
                //     initialValues[inputId] = component.props.modelValue || (component.props.options && component.props.options.length > 0 ? component.props.options[0].value : []);
                //     break;
                case 'vehiclegroup':
                case 'vehicleoption':
                    initialValues[inputId] = component.props.modelValue || [];
                    break;
                case 'checkbox':
                    initialValues[inputId] = component.props.modelValue || 'N';
                    break;
                case 'uploadbox':
                    initialValues[inputId] = {
                        fileId: '',
                        filename: '',
                        originalname: '',
                        modelValue: component.props.modelValue || []
                    };
                    break;
                case 'multiuploadbox':
                    initialValues[inputId] = Array.isArray(component.props.modelValue) ? component.props.modelValue : [];
                    break;
                case 'area':
                    initialValues[inputId] = {
                        modelValue: component.props.modelValue || []
                    };
                    break;
                case 'inputbox':
                case 'datepicker':
                case 'datepickerDual':
                case 'image':
                    initialValues[inputId] = component.props.modelValue && component.props.modelValue.length > 0 ? component.props.modelValue : '';
                    break;
                case 'notelist':
                    initialValues[inputId] = component.props.modelValue && component.props.modelValue.length > 0 ? component.props.modelValue : '';
                    break;
                default:
                    initialValues[inputId] = component.props.modelValue || '';
            }
        }
    };

    props.panels.forEach((panel) => {
        if (Array.isArray(panel.content)) {
            panel.content.forEach((row) => {
                if (Array.isArray(row.components)) {
                    row.components.forEach((component) => {
                        processComponent(component);
                    });
                }
            });
        }
    });

    return initialValues;
};

const setError = (errorData, step = '') => {
    if (!errorData || typeof errorData !== 'object') {
        console.error('Invalid error data');
        return;
    }

    const updatePromises = Object.entries(errorData).map(([inputId, errorMessage]) => {
        return new Promise((resolve) => {
            const component = findComponentByInputId(inputId);
            if (component && component.props) {
                component.props.errmsg = ''; // 기존 오류 메시지 초기화
                nextTick(() => {
                    component.props.errmsg = errorMessage; // 새로운 오류 메시지 설정
                    resolve();
                });
            } else {
                resolve();
            }
        });
    });

    Promise.all(updatePromises).then(() => {
        if (currentStep.value && step !== '') currentStep.value = step;
    });
};

const setReadonly = (res, step = '') => {
    if (!res || typeof res !== 'object') {
        console.error('Invalid Readonly data');
        return;
    }

    const updatePromises = Object.entries(res).map(([inputId]) => {
        return new Promise((resolve) => {
            const component = findComponentByInputId(inputId);
            if (component && component.props) {
                let bol = res[component.props.inputId] ? true : false;
                component.props.readonly = !bol;
                nextTick(() => {
                    component.props.readonly = bol;
                    resolve();
                });
            } else {
                resolve();
            }
        });
    });

    Promise.all(updatePromises).then(() => {
        if (currentStep.value && step !== '') currentStep.value = step;
        //console.log('Readonly settings completed');
    });
};

const setListValue = (res, step = '') => {
    console.log('Apppopup setlistvalue res>>>>>>');
    console.log(res);
    if (!res || typeof res !== 'object') {
        console.error('Invalid Readonly data');
        return;
    }
    const updatePromises = props.panels.flatMap((panel) =>
        Array.isArray(panel.content)
            ? panel.content.flat().map((components) => {
                  return new Promise((resolve) => {
                      components.components.map((component) => {
                          if (component && component.props && res.hasOwnProperty(component.props.inputId)) {
                              const bol = res[component.props.inputId];
                              component.props.dataList = [];
                              console.log('bol>>>>>>>>>>>>>>>>>');
                              console.log(bol);
                              nextTick(() => {
                                  component.props.dataList = bol;
                                  console.log('component.props.dataList');
                                  console.log(component.props.dataList);
                                  resolve();
                              });
                          } else {
                              resolve();
                          }
                      });
                  });
              })
            : []
    );
};

const setHidden = (res) => {
    const updatePromises = Object.keys(res).map((inputId) => {
        return new Promise((resolve) => {
            nextTick(() => {
                if (res[inputId] === false || res[inputId] === undefined) {
                    const newState = { ...hiddenState.value };
                    delete newState[inputId];
                    hiddenState.value = newState;
                } else {
                    hiddenState.value = { ...hiddenState.value, [inputId]: res[inputId] };
                }
                resolve();
            });
        });
    });

    return Promise.all(updatePromises).then(() => {
        //console.log('Hidden state updated:', hiddenState.value);
    });
};

const isHidden = (inputId) => {
    return hiddenState.value[inputId] === true;
};

const updateSelectOptions = (newOptions) => {
    if (!newOptions || typeof newOptions !== 'object') {
        console.error('Invalid error data');
        return;
    }

    const updatePromises = Object.entries(newOptions).map(([inputId, option]) => {
        return new Promise((resolve) => {
            const component = findComponentByInputId(inputId);
            if (component && component.props) {
                component.props.options = [];
                nextTick(() => {
                    component.props.options = option || [];
                    resolve();
                });
            } else {
                resolve();
            }
        });
    });

    Promise.all(updatePromises).then(() => {});
};

const updateComponent = (res) => {
    if (!res || typeof res !== 'object') {
        console.error('Invalid data');
        return;
    }

    Object.entries(res).forEach(([id, val]) => {
        const component = findComponentByInputId(id);
        componentValues.value[id] = component?.type === 'multiuploadbox' ? (Array.isArray(val) ? val : []) : val;
    });

    nextTick(() => {
        props.panels.forEach((panel) => {
            if (Array.isArray(panel.content)) {
                panel.content.forEach((row) => {
                    if (Array.isArray(row.components)) {
                        row.components.forEach((component) => {
                            if (component && component.props && component.props.inputId) {
                                const inputId = component.props.inputId;
                                if (res.hasOwnProperty(inputId)) {
                                    const value = res[inputId];
                                    if (component.type === 'uploadbox') {
                                        // component.props.filename = value;
                                    } else if (component.type === 'multiuploadbox') {
                                        component.props.modelValue = Array.isArray(value) ? value : [];
                                        componentValues.value[inputId] = component.props.modelValue;
                                        component.props.fileId = Array.isArray(value) ? '' : value;
                                    } else if (component.type === 'notelist') {
                                        component.props.modelValue = '';
                                        component.props.dataList = value || '';
                                    } else {
                                        componentValues.value[inputId] = value;
                                        //updateInputval(inputId, value);
                                        component.props.modelValue = value;
                                    }
                                    component.props.errmsg = '';
                                }
                            }
                        });
                    }
                });
            }
        });
        // console.log('All updates completed');
    });
};

const updateInputval = (inputId, val) => {
    return new Promise((resolve) => {
        const component = findComponentByInputId(inputId);
        if (component && component.props) {
            component.props.modelValue = '';
            nextTick(() => {
                component.props.modelValue = val;
                component.props.errmsg = '';
                componentValues.value[inputId] = val;
                resolve();
            });
        } else {
            resolve();
        }
    });
};

const updateKey = (res) => {
    if (!res || typeof res !== 'object') {
        console.error('Invalid error data');
        return;
    }

    const updatePromises = Object.entries(res).map(([inputId, val]) => {
        return new Promise((resolve) => {
            const component = findComponentByInputId(inputId);
            if (component && component.props) {
                component.props.keyVal = '';
                nextTick(() => {
                    component.props.keyVal = val.toString();
                    resolve();
                });
            } else {
                resolve();
            }
        });
    });

    Promise.all(updatePromises).then(() => {});
};

const addNewPanelAndKeepValues = (newPanel) => {
    // 기존 값 보존
    const currentValues = { ...componentValues.value };

    // 새 패널 추가
    panels.value.push(newPanel);

    // 컴포넌트 초기화 (새 패널만)
    initializeNewPanel(newPanel);

    // 기존 값 복원 및 새 패널의 값 초기화
    nextTick(() => {
        componentValues.value = {
            ...currentValues,
            [newPanel.content[0].components[0].props.inputId]: ''
        };
    });
};

const initializeNewPanel = (panel) => {
    if (Array.isArray(panel.content)) {
        panel.content.forEach((row) => {
            if (Array.isArray(row.components)) {
                row.components.forEach((component) => {
                    if (component && component.props && component.props.inputId) {
                        const inputId = component.props.inputId;
                        componentValues.value[inputId] = '';
                    }
                });
            }
        });
    }
};

const validateRequiredFields = () => {
    const errorPromises = [];
    const currentValues = getAllComponentValues();

    const processComponent = (component) => {
        if (component && component.props) {
            const { inputId, required } = component.props;
            const currentValue = currentValues[inputId];

            const isEmpty = (value) => {
                if (Array.isArray(value)) return value.length === 0;
                if (typeof value === 'object' && value !== null) return Object.keys(value).length === 0;
                return value === '' || value === null || value === undefined;
            };

            if (required && isEmpty(currentValue)) {
                errorPromises.push(
                    new Promise((resolve) => {
                        const foundComponent = findComponentByInputId(inputId);
                        if (foundComponent && foundComponent.props) {
                            foundComponent.props.errmsg = '';
                            nextTick(() => {
                                foundComponent.props.errmsg = '필수 입력 항목입니다.';
                                //console.log(`Error set for ${inputId}: ${foundComponent.props.errmsg}`);
                                resolve();
                            });
                        } else {
                            //console.log(`Component not found for ${inputId}`);
                            resolve();
                        }
                    })
                );
            } else {
                //console.log(`Validation passed for ${inputId}: required=${required}, value=${JSON.stringify(currentValue)}`);
            }
        } else {
            //console.log('Invalid component structure:', component);
        }
    };

    props.panels.forEach((panel, panelIndex) => {
        if (Array.isArray(panel.content)) {
            panel.content.forEach((row, rowIndex) => {
                if (Array.isArray(row.components)) {
                    row.components.forEach((component, componentIndex) => {
                        processComponent(component);
                    });
                } else {
                }
            });
        } else {
        }
    });

    return Promise.all(errorPromises).then(() => {
        const isValid = errorPromises.length === 0;
        //console.log(`Validation complete. Form is ${isValid ? 'valid' : 'invalid'}.`);
        return isValid;
    });
};

defineExpose({
    getAllComponentValues,
    getStepComponentValues,
    initializeComponent,
    getInitialValues,
    setError,
    setReadonly,
    setHidden,
    setListValue,
    updateSelectOptions,
    updateComponent,
    updateInputval,
    updateKey,
    addNewPanelAndKeepValues,
    validateRequiredFields,
    handleNavigation,
    getExposeValues, // inputid의 definexpose값 가져옴
    getModelValue // 특정 inputid의 modelvalue만 가져옴
});

const togglePopup = () => {
    isPopupVisibleRefs.value = !isPopupVisibleRefs.value;
};

const closePopup = () => {
    isPopupVisibleRefs.value = false;
    emit('update:isPopupVisible', false);
    //console.log('getAllComponentValues()');
    //console.log(getAllComponentValues());
    emit('input', getAllComponentValues());
};

const currentPanelTitle = computed(() => {
    return props.panels[0]?.title || '';
});

//외부로 상태값 전달달
</script>

<template>
    <div v-if="isPopupVisibleRefs" class="popup-overlay">
        <div ref="popupContent" class="popup-content" :style="{ height: contentHeight, width: contentWidth }">
            <div ref="panelPoint" class="popup-drag flex justify-between items-center mb-3" @mousedown="onMouseDown">
                <span class="title-text">{{ currentPanelTitle }}</span>
                <button class="close-button" @click.stop="closePopup">
                    <i class="pi pi-times"></i>
                </button>
            </div>

            <div class="flex justify-center w-full mx-auto stepper-container" style="flex: 1; width: 100%; overflow-y: auto">
                <div :style="{ width: contentWidth }" class="flex flex-col">
                    <div v-for="(panel, index) in panels" :key="index" v-show="currentStep === (index + 1).toString()" class="flex flex-col">
                        <div class="relative bottom-border bg-[#e5e7e] bg-white dark:bg-blue-950 p-2 mb-5 text-black flex-grow flex flex-col">
                            <div class="flex-grow overflow-y-auto" :style="{ marginLeft: containerMargin.left, marginRight: containerMargin.right }">
                                <div v-if="typeof panel.content === 'string'" v-html="panel.content"></div>

                                <template v-else>
                                    <div class="flex flex-col">
                                        <template v-for="(row, rowIndex) in panel.content" :key="rowIndex">
                                            <component :is="renderRow(row)" />
                                        </template>
                                    </div>
                                </template>
                            </div>
                        </div>

                        <div v-if="panel.buttons && panel.buttons.length" class="text-right top-2 right-4 z-10">
                            <button
                                v-for="(button, buttonIndex) in panel.buttons"
                                :key="buttonIndex"
                                @click="button.onClick(getAllComponentValues(), actionRefs)"
                                :class="[
                                    button.visible === false ? 'hidden' : '',
                                    'panel-button mt-1 ml-2',
                                    button.label === '삭제' ? 'button-deleted' : button.label === '저장' ? 'button-saved' : button.label === '수정' ? 'button-saved' : '',
                                    button.visibleAction && Array.isArray(button.visibleAction) ? (button.visibleAction.indexOf(actionRefs) === -1 ? 'hidden' : '') : ''
                                ]"
                            >
                                {{ button.label }}
                            </button>
                        </div>
                    </div>

                    <div v-if="panels.length > 1 && !nonButtonRefs" class="flex justify-between">
                        <button v-if="currentStep !== '1'" class="custom-button px-7 py-2" @click="handleNavigation('prev')">이전</button>
                        <div v-else class="w-20"></div>
                        <button v-if="currentStep !== panels.length.toString()" class="custom-button px-7 py-2" @click="handleNavigation('next')">다음</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.stepper-container {
    height: calc(100vh - 15rem);
}

.stepper-container::-webkit-scrollbar {
    height: calc(100vh - 15rem);
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.timeline-item {
    font-family: 'SCoreDream400';
    font-size: 1rem;
}

.timeline-item:hover,
.timeline-item.active {
    font-family: 'SCoreDream500';
}

.custom-button {
    font-family: 'SCoreDream500';
    background-color: #0038ea;
    color: white;
    border-radius: 0.3rem;
    cursor: pointer;
    width: 7rem;
    padding: 0.4rem;
    opacity: 0.85;
    transition: border-color 0.3s ease;
}

.custom-button:hover {
    opacity: 1;
}

.panel-button {
    background-color: white;
    color: black;
    border: solid;
    border-radius: 0.375rem;
    border-color: #e5e7eb;
    border-width: 0.1rem;
    height: 3rem;
    cursor: pointer;
    width: 8rem;
    /* display: flex; */
    align-items: center;
    justify-content: center;
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
    transition: border-color 0.3s ease;
}

.panel-button:hover {
    opacity: 1;
}

.bottom-border {
    border: none;
    border-bottom: 0.13rem solid #e5e7eb;
    box-sizing: border-box;
    padding-bottom: 1.5rem;
}

.button-deleted {
    background-color: red;
    opacity: 0.8;
    color: white;
}

.button-saved {
    background-color: #0038ea;
    opacity: 0.85;
    color: white;
}

.panel-actionbutton {
    padding: 0.3rem;
    background-color: #f9fafb;
    border: solid;
    border-radius: 0.375rem;
    border-color: #e5e7eb;
    border-width: 0.1rem;
    cursor: pointer;
    width: 10rem;
    /* display: flex; */
    align-items: center;
    justify-content: center;
    font-family: 'SCoreDream500';
    transition: border-color 0.3s ease;
}

.panel-actionbutton:hover {
    border-color: #0038ea;
}

.process-status-label {
    display: block;
    font-family: 'SCoreDream500';
    /* margin-bottom: 2.5rem; */
    font-size: 1.15rem;
}

.status-text {
    font-family: 'SCoreDream600';
    font-size: 1.15rem;
    color: #0038ea;
}
.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
}

.popup-drag {
    cursor: move;
    user-select: none;
}

.popup-content {
    position: absolute;
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.6rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    text-align: left;
    display: flex;
    flex-direction: column;
}

.title-text {
    font-size: 1.3rem;
    font-family: 'SCoreDream500';
    flex-shrink: 0; /* Prevent title from shrinking */
    padding: 0.1rem 0 0.1rem 0;
}
.close-button {
    font-size: 1.1rem;
    font-family: 'SCoreDream300';
    color: #b7b9bd;
    cursor: pointer;
    background: none; /* Remove background */
    border: none; /* Remove border */
    padding: 0;
    line-height: 1; /* Align text vertically */
}

.close-button:hover {
    color: #000; /* Slightly darker on hover */
}
</style>
